import { jsx, jsxs } from "react/jsx-runtime";
import { useState, useRef, useCallback, useEffect } from "react";
import { isTouchDevice, clamp } from "@perxis-auto/shared";
import { useSprings, animated } from "@react-spring/web";
import { useDrag } from "@use-gesture/react";
import hash from "object-hash";
import carFallback from "../../assets/images/carFallback.svg.js";
import classnames from "../../utils/classnames.js";
import Col from "../Grid/Col.js";
import Row from "../Grid/Row.js";
import './CarTile.css';import '../Grid/Grid.css';/* empty css                */
import Icon from "../Icon/Icon.js";
import Typography from "../Typography/Typography.js";
import CarTileImage from "./CarTileImage.js";
/* empty css             */
const CarTileGallery = ({
  images = [],
  counterPrefix = "Еще",
  counterPostfix = "фото",
  className,
  wrapperClassName,
  onTouchStart,
  onTouchEnd
}) => {
  var _a, _b, _c, _d;
  const [containerRef, setContainerRef] = useState(null);
  const [width, setWidth] = useState(575);
  const [currentImage, setCurrentImage] = useState(0);
  const currentImageRef = useRef(currentImage);
  const slidesForPreview = images.slice(0, 5);
  const countHiddenImages = images.length - slidesForPreview.length;
  const isMobile = isTouchDevice();
  const isShowStub = currentImage === 4 && !!countHiddenImages;
  const [animateSlides, api] = useSprings(
    slidesForPreview.length,
    (i) => ({
      x: i * width,
      scale: 1,
      display: "block"
    }),
    [width]
  );
  const bind = useDrag(
    ({ active, movement: [mx], direction: [xDir], cancel }) => {
      if (currentImageRef.current === 0 && mx > 0 || (currentImageRef.current === images.length - 1 || isShowStub) && mx < 0) {
        cancel();
      }
      if (active && Math.abs(mx) > width / 4) {
        updateCurrentImage(clamp(currentImageRef.current + (xDir > 0 ? -1 : 1), 0, slidesForPreview.length - 1));
        cancel();
      }
      api.start((i) => {
        if (i < currentImageRef.current - 1 || i > currentImageRef.current + 1) {
          return { display: "none" };
        }
        const x = (i - currentImageRef.current) * width + (active ? mx : 0);
        return { x, display: "block" };
      });
    },
    {
      preventScrollAxis: "y",
      eventOptions: { capture: true }
    }
  );
  const updateCurrentImage = useCallback((newImage) => {
    setCurrentImage(newImage);
    currentImageRef.current = newImage;
  }, []);
  const onBodyRefChange = useCallback((node) => setContainerRef(node), []);
  const resizeHandler = useCallback(() => {
    api.update({ x: 0, reset: true });
    setCurrentImage(0);
    currentImageRef.current = 0;
    if (containerRef) {
      setWidth(containerRef == null ? void 0 : containerRef.offsetWidth);
    }
  }, [api, containerRef]);
  useEffect(() => {
    if (containerRef) {
      resizeHandler();
      const resizeObserver = new ResizeObserver(resizeHandler);
      resizeObserver.observe(containerRef);
      return () => resizeObserver.disconnect();
    }
  }, [containerRef, resizeHandler]);
  if (images.length < 2) {
    return /* @__PURE__ */ jsx(
      CarTileImage,
      {
        alt: (_a = images == null ? void 0 : images[0]) == null ? void 0 : _a.title,
        sizes: (_b = images == null ? void 0 : images[0]) == null ? void 0 : _b.sizes,
        src: (_c = images == null ? void 0 : images[0]) == null ? void 0 : _c.original,
        srcSet: (_d = images == null ? void 0 : images[0]) == null ? void 0 : _d.srcSet,
        className,
        wrapperClassName
      }
    );
  }
  const renderSpots = () => {
    return /* @__PURE__ */ jsx(Row, { className: classnames("perxis-auto-car-tile-gallery-spots"), children: slidesForPreview.map((item, index) => {
      return /* @__PURE__ */ jsx(
        Col,
        {
          flex: 1,
          onMouseEnter: !isMobile ? () => updateCurrentImage(index) : void 0,
          onMouseLeave: !isMobile ? () => updateCurrentImage(0) : void 0,
          background: "default",
          className: classnames([
            "perxis-auto-car-tile-gallery-spot",
            {
              "perxis-auto-car-tile-gallery-spot--active": index === currentImage,
              "perxis-auto-car-tile-gallery-spot--visible": isMobile
            }
          ]),
          children: /* @__PURE__ */ jsx("div", { className: classnames("perxis-auto-car-tile-gallery-spot-line") })
        },
        hash(item)
      );
    }) });
  };
  if (isMobile) {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ...bind(),
        ref: onBodyRefChange,
        className: classnames("perxis-auto-car-tile-gallery-wrapper", wrapperClassName),
        onTouchStart,
        onTouchEnd,
        children: [
          renderSpots(),
          animateSlides.map(({ x, display }, i) => /* @__PURE__ */ jsx(
            animated.div,
            {
              style: { display, x },
              className: classnames("perxis-auto-car-tile-gallery-wrapper-image", className),
              children: /* @__PURE__ */ jsx(
                animated.img,
                {
                  alt: images[i].title,
                  src: images[i].original || carFallback,
                  srcSet: images[i].srcSet,
                  sizes: images[i].sizes,
                  onError: ({ currentTarget }) => {
                    currentTarget.onerror = null;
                    currentTarget.src = carFallback;
                    currentTarget.srcset = "";
                  },
                  className: classnames("perxis-auto-car-tile-gallery-image-mobile", className)
                }
              )
            },
            x.id
          )),
          isShowStub ? /* @__PURE__ */ jsxs("div", { className: classnames("perxis-auto-car-tile-gallery-stub"), children: [
            /* @__PURE__ */ jsx(Icon, { name: "photo", size: 48 }),
            /* @__PURE__ */ jsxs(Typography.Text, { children: [
              counterPrefix,
              " ",
              countHiddenImages,
              " ",
              counterPostfix
            ] })
          ] }) : null
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: classnames("perxis-auto-car-tile-gallery-wrapper", wrapperClassName), children: [
    /* @__PURE__ */ jsx(
      "img",
      {
        alt: images[currentImage].title,
        src: images[currentImage].original || carFallback,
        srcSet: images[currentImage].srcSet,
        sizes: images[currentImage].sizes,
        onError: ({ currentTarget }) => {
          currentTarget.onerror = null;
          currentTarget.src = carFallback;
          currentTarget.srcset = "";
        },
        className: classnames("perxis-auto-car-tile-gallery-image", className)
      }
    ),
    renderSpots(),
    isShowStub ? /* @__PURE__ */ jsxs("div", { className: classnames("perxis-auto-car-tile-gallery-stub"), children: [
      /* @__PURE__ */ jsx(Icon, { name: "photo", size: 48 }),
      /* @__PURE__ */ jsxs(Typography.Text, { children: [
        counterPrefix,
        " ",
        countHiddenImages,
        " ",
        counterPostfix
      ] })
    ] }) : null
  ] });
};
export {
  CarTileGallery as default
};
//# sourceMappingURL=CarTileGallery.js.map
